import type {
  AiFormDefinition,
  CreateFormDto,
  CreateFormFieldDto,
  CreateFormSectionDto,
  Form,
  GeneratedFormResult,
  FormAccessToken,
  FormField,
  FormSection,
  FormStatus,
  FormSubmission,
  GenerateAccessTokensDto,
  PaginatedResponse,
  PublicFormResponse,
  SubmitFormDto,
  UpdateFormDto,
  UpdateFormFieldDto,
  UpdateFormSectionDto,
  ValidateAccessDto,
} from '@scrambl/shared'

import { apiClient, apiClientPublic } from './api-client'

export interface FormSubmissionAnalytics {
  totalSubmissions: number
  submissionsThisWeek: number
  submissionsLastWeek: number
  statusBreakdown: Record<string, number>
  submissionsByDay: Array<{ date: string; count: number }>
  fieldCompletionCounts: Record<string, number>
  recentSubmissions: Array<{
    id: string
    submitterName?: string
    submitterEmail?: string
    createdAt: string
    status: string
  }>
}

// Helper function to clean and normalize API parameters
function cleanParams(params: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {}

  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length > 0) {
        cleaned[key] = value.join(',')
      } else if (typeof value === 'object') {
        cleaned[key] = JSON.stringify(value)
      } else {
        cleaned[key] = String(value)
      }
    }
  })

  return cleaned
}

// Forms API singleton
class FormsApi {
  private static instance: FormsApi | null = null

  public static getInstance(): FormsApi {
    if (!FormsApi.instance) {
      FormsApi.instance = new FormsApi()
    }
    return FormsApi.instance
  }

  // ==================
  // Module Counts
  // ==================

  async getModuleCounts(): Promise<{ allForms: number }> {
    return apiClient<{ allForms: number }>('/forms/module-counts')
  }

  // ==================
  // Form CRUD
  // ==================

  async getAll(
    params: {
      page?: number
      limit?: number
      status?: FormStatus
      search?: string
      columnFilters?: Record<string, unknown>
    } = {},
  ): Promise<PaginatedResponse<Form>> {
    const queryParams = cleanParams({
      page: params.page,
      limit: params.limit,
      status: params.status,
      search: params.search,
      columnFilters: params.columnFilters,
    })

    const queryString = new URLSearchParams(queryParams).toString()
    return apiClient<PaginatedResponse<Form>>(
      `/forms${queryString ? `?${queryString}` : ''}`,
    )
  }

  async getById(id: string): Promise<Form> {
    return apiClient<Form>(`/forms/${id}`)
  }

  async getBySlug(slug: string): Promise<Form> {
    return apiClient<Form>(`/forms/slug/${slug}`)
  }

  async checkSlugAvailability(
    slug: string,
    excludeId?: string,
  ): Promise<{ available: boolean; reason?: string }> {
    const queryString = excludeId ? `?excludeId=${excludeId}` : ''
    return apiClient<{ available: boolean; reason?: string }>(
      `/forms/check-slug/${slug}${queryString}`,
    )
  }

  async create(data: CreateFormDto): Promise<Form> {
    return apiClient<Form>('/forms', {
      method: 'POST',
      body: data,
    })
  }

  /**
   * Generate (or edit) a form definition with AI. Pass existingDefinition to
   * modify a form (serialized via useFormBuilder.getAiDefinition); omit it to
   * create from scratch. Invalid AI output returns { valid: false, issues }.
   */
  async generateWithAi(params: {
    prompt: string
    targetType?: 'MODULE' | 'COLLECTION'
    targetModuleType?: string
    existingDefinition?: AiFormDefinition
  }): Promise<GeneratedFormResult> {
    const response = await apiClient<{ data: GeneratedFormResult }>(
      '/ai/generate-form',
      { method: 'POST', body: params },
    )
    return response.data
  }

  async update(id: string, data: UpdateFormDto): Promise<Form> {
    return apiClient<Form>(`/forms/${id}`, {
      method: 'PUT',
      body: data,
    })
  }

  async delete(id: string): Promise<void> {
    await apiClient<void>(`/forms/${id}`, {
      method: 'DELETE',
    })
  }

  async bulkDelete(ids: string[]): Promise<{ deletedCount: number }> {
    return apiClient<{ deletedCount: number }>('/forms/bulk-delete', {
      method: 'POST',
      body: { ids },
    })
  }

  // ==================
  // Form Actions
  // ==================

  async publish(id: string): Promise<Form> {
    return apiClient<Form>(`/forms/${id}/publish`, {
      method: 'POST',
    })
  }

  async close(id: string): Promise<Form> {
    return apiClient<Form>(`/forms/${id}/close`, {
      method: 'POST',
    })
  }

  async duplicate(id: string): Promise<Form> {
    return apiClient<Form>(`/forms/${id}/duplicate`, {
      method: 'POST',
    })
  }

  async uploadFormImage(
    id: string,
    file: File,
    placement: 'above-title' | 'below-title' | 'above-button' | 'below-button',
    type: 'logo' | 'header' | 'footer',
  ): Promise<Form> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('placement', placement)
    formData.append('type', type)

    return apiClient<Form>(`/forms/${id}/upload-image`, {
      method: 'POST',
      body: formData,
    })
  }

  async uploadFormContentImage(
    id: string,
    file: File,
  ): Promise<{ url: string; key?: string }> {
    const formData = new FormData()
    formData.append('file', file)

    return apiClient<{ url: string; key?: string }>(
      `/forms/${id}/upload-content-image`,
      {
        method: 'POST',
        body: formData,
      },
    )
  }

  // ==================
  // Form Fields
  // ==================

  async createField(
    formId: string,
    data: CreateFormFieldDto,
  ): Promise<FormField> {
    return apiClient<FormField>(`/forms/${formId}/fields`, {
      method: 'POST',
      body: data,
    })
  }

  async updateField(
    formId: string,
    fieldId: string,
    data: UpdateFormFieldDto,
  ): Promise<FormField> {
    return apiClient<FormField>(`/forms/${formId}/fields/${fieldId}`, {
      method: 'PUT',
      body: data,
    })
  }

  async deleteField(formId: string, fieldId: string): Promise<void> {
    await apiClient<void>(`/forms/${formId}/fields/${fieldId}`, {
      method: 'DELETE',
    })
  }

  async reorderFields(formId: string, fieldIds: string[]): Promise<void> {
    await apiClient<void>(`/forms/${formId}/fields/reorder`, {
      method: 'POST',
      body: { fieldIds },
    })
  }

  // ==================
  // Form Sections
  // ==================

  async createSection(
    formId: string,
    data: CreateFormSectionDto,
  ): Promise<FormSection> {
    return apiClient<FormSection>(`/forms/${formId}/sections`, {
      method: 'POST',
      body: data,
    })
  }

  async updateSection(
    formId: string,
    sectionId: string,
    data: UpdateFormSectionDto,
  ): Promise<FormSection> {
    return apiClient<FormSection>(`/forms/${formId}/sections/${sectionId}`, {
      method: 'PUT',
      body: data,
    })
  }

  async deleteSection(formId: string, sectionId: string): Promise<void> {
    await apiClient<void>(`/forms/${formId}/sections/${sectionId}`, {
      method: 'DELETE',
    })
  }

  async reorderSections(formId: string, sectionIds: string[]): Promise<void> {
    await apiClient<void>(`/forms/${formId}/sections/reorder`, {
      method: 'POST',
      body: { sectionIds },
    })
  }

  // ==================
  // Submissions
  // ==================

  async getSubmissions(
    formId: string,
    params: {
      page?: number
      limit?: number
      status?: string
    } = {},
  ): Promise<PaginatedResponse<FormSubmission>> {
    const queryParams = cleanParams({
      page: params.page,
      limit: params.limit,
      status: params.status,
    })

    const queryString = new URLSearchParams(queryParams).toString()
    return apiClient<PaginatedResponse<FormSubmission>>(
      `/forms/${formId}/submissions${queryString ? `?${queryString}` : ''}`,
    )
  }

  async getSubmissionAnalytics(
    formId: string,
    timezone?: string,
  ): Promise<FormSubmissionAnalytics> {
    const query = timezone
      ? `?${new URLSearchParams({ timezone }).toString()}`
      : ''
    return apiClient<FormSubmissionAnalytics>(
      `/forms/${formId}/submissions-analytics${query}`,
    )
  }

  async getSubmission(
    formId: string,
    submissionId: string,
  ): Promise<FormSubmission> {
    return apiClient<FormSubmission>(
      `/forms/${formId}/submissions/${submissionId}`,
    )
  }

  async deleteSubmission(formId: string, submissionId: string): Promise<void> {
    await apiClient<void>(`/forms/${formId}/submissions/${submissionId}`, {
      method: 'DELETE',
    })
  }

  async bulkDeleteSubmissions(
    formId: string,
    submissionIds: string[],
  ): Promise<{ deletedCount: number }> {
    return apiClient<{ deletedCount: number }>(
      `/forms/${formId}/submissions/bulk-delete`,
      {
        method: 'POST',
        body: { ids: submissionIds },
      },
    )
  }

  // ==================
  // Access Tokens
  // ==================

  /**
   * Mint invite tokens. The returned rows carry `token` — the ONLY time the
   * plaintext exists, since tokens are hashed at rest. If the caller needs to
   * show or copy a link, it must do so from this response; afterwards only
   * `tokenPrefix` is available.
   */
  async generateAccessTokens(
    formId: string,
    data: GenerateAccessTokensDto,
  ): Promise<FormAccessToken[]> {
    return apiClient<FormAccessToken[]>(`/forms/${formId}/tokens`, {
      method: 'POST',
      body: data,
    })
  }

  /**
   * List existing invite tokens. Rows have NO `token` — only `tokenPrefix`,
   * the non-secret handle. To give someone a working link again, revoke the
   * token and generate a new one.
   */
  async getAccessTokens(formId: string): Promise<FormAccessToken[]> {
    return apiClient<FormAccessToken[]>(`/forms/${formId}/tokens`)
  }

  async revokeAccessToken(formId: string, tokenId: string): Promise<void> {
    await apiClient<void>(`/forms/${formId}/tokens/${tokenId}`, {
      method: 'DELETE',
    })
  }

  // ==================
  // Public Form Access
  // ==================

  async getPublicForm(slug: string): Promise<PublicFormResponse> {
    return apiClientPublic<PublicFormResponse>(`/public/forms/${slug}`)
  }

  async validateAccess(
    slug: string,
    data: ValidateAccessDto,
  ): Promise<{ valid: boolean; email?: string; accessToken?: string }> {
    return apiClientPublic<{
      valid: boolean
      email?: string
      accessToken?: string
    }>(`/public/forms/${slug}/validate-access`, {
      method: 'POST',
      body: data,
    })
  }

  async sendVerificationCode(
    slug: string,
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    return apiClientPublic<{ success: boolean; message: string }>(
      `/public/forms/${slug}/send-verification-code`,
      {
        method: 'POST',
        body: { email },
      },
    )
  }

  async verifyCode(
    slug: string,
    email: string,
    code: string,
  ): Promise<{ valid: boolean; email: string; accessToken?: string }> {
    return apiClientPublic<{
      valid: boolean
      email: string
      accessToken?: string
    }>(`/public/forms/${slug}/verify-code`, {
      method: 'POST',
      body: { email, code },
    })
  }

  async submitForm(
    slug: string,
    data: SubmitFormDto,
    accessToken?: string,
    // Origin of the site hosting the form iframe (the customer's domain).
    // Sent so the API can enforce the form's embedDomains allowlist — the
    // server can't see it otherwise (the iframe's own Origin is app.scrambl.com).
    embedOrigin?: string,
  ): Promise<FormSubmission> {
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers['x-access-token'] = accessToken
    }
    if (embedOrigin) {
      headers['x-embed-origin'] = embedOrigin
    }

    return apiClientPublic<FormSubmission>(`/public/forms/${slug}/submit`, {
      method: 'POST',
      body: data,
      headers,
    })
  }

  // ==================
  // Public File Upload
  // ==================

  async uploadPublicFile(
    slug: string,
    fieldId: string,
    file: File,
    accessToken?: string,
    onProgress?: (progress: number) => void,
  ): Promise<{
    success: boolean
    file: {
      key: string
      thumbnailKey?: string
      fileName: string
      contentType: string
      size: number
      url: string
      thumbnailUrl?: string
    }
  }> {
    const formData = new FormData()
    formData.append('file', file)

    // Get the base URL for public API
    const baseUrl = import.meta.env.VITE_API_URL || '/api'

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch {
            reject(new Error('Invalid response format'))
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText)
            reject(new Error(error.message || 'Upload failed'))
          } catch {
            reject(new Error('Upload failed'))
          }
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'))
      })

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'))
      })

      xhr.open(
        'POST',
        `${baseUrl}/public/forms/${slug}/upload?fieldId=${encodeURIComponent(fieldId)}`,
      )

      if (accessToken) {
        xhr.setRequestHeader('x-access-token', accessToken)
      }

      xhr.send(formData)
    })
  }

  async getPublicFileUrl(
    slug: string,
    fileKey: string,
    accessToken?: string,
  ): Promise<{ url: string }> {
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers['x-access-token'] = accessToken
    }

    return apiClientPublic<{ url: string }>(
      `/public/forms/${slug}/file/${encodeURIComponent(fileKey)}`,
      {
        headers,
      },
    )
  }
}

export const formsApi = FormsApi.getInstance()
